/**
 * İstemci cihaz profili — **tek kaynak**.
 *
 * ## Neden ayrı bir profil?
 *
 * Masaüstü profili (`web-chromium`) HEVC'yi kabul eder ve çözünürlük sınırı
 * koymaz. Telefon **her zaman** VPS'e uzaktan bağlanır; bu profille 4K HEVC
 * dosya dönüştürülmeden 50–100 Mbit/s ile çekilir ve hücre bağlantısı ilk
 * saniyelerde tükenir.
 *
 * Mobil profil (`web-mobile`): yalnız h264/aac mp4, 720p tavanı, 4 Mbit/s
 * bağlantı bütçesi, tek eşzamanlı akış.
 *
 * ## Profil nasıl seçilir?
 *
 * İki sinyal birlikte:
 *   - **Kullanıcı aracısı**: Android/iPhone/iPod/Windows Phone.
 *   - **İşaretçi türü + ekran genişliği**: dokunmatik (`coarse`) ve dar ekran.
 *
 * iPadOS 13+ "masaüstü" UA gönderir; ekranı geniş olduğu için **masaüstü**
 * sayılır (doğru: iPad büyük ekranlıdır ve 4K HEVC'yi oynatabilir).
 */

/** Masaüstü tarayıcı profili. */
export const DESKTOP_PROFILE_ID = 'web-chromium';

/** Mobil tarayıcı profili. */
export const MOBILE_PROFILE_ID = 'web-mobile';

export type ClientProfileId = typeof DESKTOP_PROFILE_ID | typeof MOBILE_PROFILE_ID;

/** Profil seçimini belirleyen çevre bilgisi. */
export interface ClientEnvironment {
  userAgent: string;
  /** CSS piksel cinsinden görünüm genişliği. */
  viewportWidth: number;
  /** Dokunmatik birincil işaretçi mi? */
  coarsePointer: boolean;
}

const MOBILE_UA = /Android|iPhone|iPod|Windows Phone|BlackBerry|Opera Mini|IEMobile|Mobile/i;

/**
 * Bu cihaz mobil mi?
 *
 * Saf fonksiyon (DOM'a bağlı değil) → test edilebilir.
 */
export function isMobileEnvironment(env: ClientEnvironment): boolean {
  if (MOBILE_UA.test(env.userAgent)) return true;
  // Dokunmatik + dar ekran: tablette de küçük ekranlı oyuncaklar yakalanır.
  return env.coarsePointer && env.viewportWidth <= 820;
}

/** Tarayıcının geçerli çevresini okur (test edilebilir olması için ayrı). */
export function readClientEnvironment(): ClientEnvironment {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { userAgent: '', viewportWidth: 1280, coarsePointer: false };
  }
  const coarsePointer =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  return {
    userAgent: navigator.userAgent ?? '',
    viewportWidth: window.innerWidth,
    coarsePointer,
  };
}

/**
 * Bu cihazın profili.
 *
 * `environment` verilmezse tarayıcıdan okunur.
 */
export function clientProfileId(environment?: ClientEnvironment): ClientProfileId {
  const env = environment ?? readClientEnvironment();
  return isMobileEnvironment(env) ? MOBILE_PROFILE_ID : DESKTOP_PROFILE_ID;
}
